import classNames from 'classnames';
import React, { useRef, useContext } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import variables from '../../theme/variables';
import tapes from '../../config/tapes';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/ArrowLeftIcon.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/ArrowRightIcon.svg';
import throttle from 'lodash.throttle';
import SelectedTape from './SelectedTape';
import Lottie from '../common/Lottie';
import LoaderAnimation from '../../assets/lotties/Loader.json';
import { State } from '../../contexts/GlobalState';

const tapeGap = 140;
const wrapperHeight = 6000;

const useStyles = createUseStyles(
    {
        loader: {
            position: 'fixed',
            left: 0,
            top: 0,
            height: `100%`,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
        },
        wrap: {
            position: 'relative',
            background: '#151515',
            color: variables.palette.white,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            transition: 'opacity 0.5s ease-in-out',
        },

        rack: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            perspective: 7000,
        },

        tape: {
            width: 'calc(275px * var(--scaleFactor))',
            height: 'calc(425px * var(--scaleFactor))',
            position: 'absolute',
            top: 'calc(50% - 0.5 * 275px * var(--scaleFactor))', // 275,
            left: '50%',
            transformStyle: 'preserve-3d',
            // transform:  // set in JS ,
            transition: 'transform 0.2s linear',
            transformOrigin: 'center center',

            '@media (min-width: 768px)': {
                top: '50%',
            },

            '&:hover': {
                cursor: 'pointer',
            },
        },

        tapeSpine: {
            position: 'absolute',
            height: 'calc(425px * var(--scaleFactor))',
            width: 'calc(80px * var(--scaleFactor))',
            background: 'rgba(255, 0, 0, 1)',
            transformOrigin: 'center right',
            transform: `translateX(calc(-80px * var(--scaleFactor))) rotateY(-90deg)`,
            borderTop: `calc(8px * var(--scaleFactor)) solid #484848`,
            borderBottom: `calc(8px * var(--scaleFactor)) solid #484848`,
            // backgroundImage: `url(${TestCoverSpine})`,
            backgroundSize: '102% 102%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
        },

        tapeFront: {
            position: 'absolute',
            height: 'calc(425px * var(--scaleFactor))',
            width: 'calc(275px * var(--scaleFactor))',
            background: 'white',
            borderTop: `calc(8px * var(--scaleFactor)) solid #484848`,
            borderBottom: `calc(8px * var(--scaleFactor)) solid #484848`,
            // backgroundImage: `url(${TestCoverFront})`,
            backgroundSize: '102% 102%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
        },

        tapeTop: {
            position: 'absolute',
            width: 'calc(275px * var(--scaleFactor))',
            height: 'calc(80px * var(--scaleFactor))',
            transformOrigin: 'bottom center',
            transform: `translateY(calc(-80px * var(--scaleFactor))) rotateX(90deg)`,
            background: '#181818',
        },

        arrows: {
            position: 'fixed',
            bottom: 24,
            left: 16,
            transition: 'opacity 0.3s ease-in-out',

            '@media (min-width: 768px)': {
                bottom: 60,
                left: 60,
            },
        },

        btnLeft: {
            marginRight: 16,
        },

        detailWrap: {
            position: 'fixed',
            zIndex: 5,
            bottom: 72,
            left: 16,
            width: 'calc(100% - 32px)',
            transition: 'opacity 0.3s ease-in-out',

            '@media (min-width: 768px)': {
                bottom: 130,
                left: 60,
                width: 'calc(50vw - 60px)',
                maxWidth: 400,
            },
        },

        tapeDetail: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            transition: 'all 0.2s ease-in-out',
            pointerEvents: 'none',
        },

        tapeDetailPrev: {
            opacity: 0,
            transform: 'translateY(-50px)',

            '& $tapeNo': { transform: 'translateY(-50px)' },
            '& $tapeTitle': { transform: 'translateY(-25px)' },
        },

        tapeDetailActive: {
            opacity: 1,
            transform: 'translateY(0)',
        },

        tapeDetailNext: {
            opacity: 0,
            transform: 'translateY(50px)',

            '& $tapeTitle': { transform: 'translateY(25px)' },
            '& $tapeSubtitle': { transform: 'translateY(50px)' },
        },

        tapeNo: {
            transition: 'all 0.2s ease-in-out',
            fontFamily: '"Segoe UI", sans-serif',
            fontWeight: 600,
            fontSize: 16,
        },
        tapeTitle: {
            transition: 'all 0.2s ease-in-out',
            letterSpacing: '-0.5px',
            fontSize: 18,
            lineHeight: '26px',

            '@media (min-width: 768px)': {
                fontSize: 32,
                lineHeight: '40px',
            },
        },
    },
    { name: 'TapeDeck' }
);

export default function TapeDeck() {
    const classes = useStyles();
    const { muted, setSoundscapeMuted } = useContext(State);

    const [state, setState] = useState('init'); // init | in | out
    const images = useRef({});

    useEffect(() => {
        tapes.forEach((tape) => {
            images.current[tape.tapeNo] = {};
            images.current[tape.tapeNo].front = 0;
            images.current[tape.tapeNo].spine = 0;
        });
        tapes.forEach((tape) => {
            let imgFront = new Image();
            imgFront.onload = () => {
                images.current[tape.tapeNo].front = 1;
                checkAllLoaded();
            };
            imgFront.src = `${window.rootUrl}/img/tapes/front/${tape.tapeNo}.webp`;
            let imgSpine = new Image();
            imgSpine.onload = () => {
                images.current[tape.tapeNo].spine = 1;
                checkAllLoaded();
            };
            imgSpine.src = `${window.rootUrl}/img/tapes/spine/${tape.tapeNo}.webp`;
        });
        function checkAllLoaded() {
            for (let key in images.current) {
                if (
                    images.current[key].front === 0 ||
                    images.current[key].spine === 0
                )
                    return;
            }
            setState('in');
        }
    }, []);

    useEffect(() => {
        setSoundscapeMuted(true);
        track.current = getRandomNextTrack();

        audioRef.current.addEventListener('ended', () => {
            audioRef.current.pause();
            track.current = getRandomNextTrack();
            audioRef.current.currentTime = 0;
            audioRef.current.play();
        });

        return () => {
            setSoundscapeMuted(false);
        };
    }, []);

    const getRandomNextTrack = () => {
        const tracks = [
            `${window.rootUrl}/audio/Kimiko0.mp3`,
            `${window.rootUrl}/audio/Kimiko1.mp3`,
            `${window.rootUrl}/audio/Kimiko2.mp3`,
            `${window.rootUrl}/audio/Kimiko3.mp3`,
        ];

        const currentIndex = tracks.findIndex((el) => el === track.current);

        if (currentIndex > -1) {
            tracks.splice(currentIndex, 1);
        }

        const random = Math.floor(Math.random() * tracks.length);

        console.log('next audio track: ', tracks[random]);
        return tracks[random];
    };

    const audioRef = useRef();
    const track = useRef(null);

    const wrapRef = useRef();
    const [left, setLeft] = useState(0);
    const [active, setActive] = useState(0);
    const [selected, setSelected] = useState(null);
    const [selectedTapeModal, setSelectedTapeModal] = useState(false);

    const [scaleFactor, setScaleFactor] = useState(0.75);
    const [translateX, setTranslateX] = useState('15vw');

    useEffect(() => {
        function checkScreenWidth() {
            const w = window.innerWidth;
            if (w < 768) {
                setScaleFactor(0.5);
                setTranslateX('0vw');
            } else if (w < 1024) {
                setScaleFactor(0.625);
                setTranslateX('12vw');
            } else {
                setScaleFactor(0.75);
                setTranslateX('12vw');
            }
        }

        window.addEventListener('resize', checkScreenWidth);
        checkScreenWidth();

        return () => {
            window.removeEventListener('resize', checkScreenWidth);
        };
    }, []);

    useEffect(() => {
        window.addEventListener(
            'scroll',
            throttle(function () {
                let scrollHeight = Math.max(
                    document.body.scrollHeight,
                    document.documentElement.scrollHeight,
                    document.body.offsetHeight,
                    document.documentElement.offsetHeight,
                    document.body.clientHeight,
                    document.documentElement.clientHeight
                );
                const scrollPercentage =
                    window.scrollY / (scrollHeight - window.innerHeight);

                setLeft(tapeGap * (tapes.length - 1) * scrollPercentage);
                setActive(Math.round(scrollPercentage * tapes.length));
            }, 100)
        );
    }, [wrapRef.current]);

    const goPrev = () => {
        let scrollHeight = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.offsetHeight,
            document.body.clientHeight,
            document.documentElement.clientHeight
        );

        const fullScroll = scrollHeight - window.innerHeight;
        const percentage = (active - 1) / tapes.length;

        window.scrollTo({
            top: Math.floor(percentage * fullScroll),
            behavior: 'smooth',
        });
    };

    const goNext = () => {
        let scrollHeight = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.offsetHeight,
            document.body.clientHeight,
            document.documentElement.clientHeight
        );

        const fullScroll = scrollHeight - window.innerHeight;
        const percentage = (active + 1) / tapes.length;

        window.scrollTo({
            top: Math.floor(percentage * fullScroll),
            behavior: 'smooth',
        });
    };

    const openTape = (index) => {
        const theTape = document.querySelector(`.tape-${index}`);
        theTape.style.transition = 'transform 0.4s linear';
        setSelected(index);
        setSelectedTapeModal(true);
    };

    const onTapeLeave = () => {
        const index = selected;
        const theTape = document.querySelector(`.tape-${index}`);
        setSelected(null);
        setTimeout(() => {
            theTape.style.transition = 'transform 0.2s linear';
        }, 600);
    };

    return (
        <>
            {state === 'init' && (
                <div className={classes.loader}>
                    <Lottie
                        animationData={LoaderAnimation}
                        width="100%"
                        height="100%"
                    />
                </div>
            )}
            <div
                className={classes.wrap}
                style={{
                    opacity: state === 'in' ? 1 : 0,
                    height: state === 'init' ? 0 : wrapperHeight,
                    '--scaleFactor': scaleFactor,
                }}
            >
                <audio
                    src={`${track.current}`}
                    autoPlay
                    ref={audioRef}
                    muted={muted}
                />
                <div className={classes.rack}>
                    {tapes.map((tape, index) => (
                        <div
                            onClick={(e) => openTape(index)}
                            className={classNames(
                                classes.tape,
                                `tape-${index}`
                            )}
                            key={index}
                            style={{
                                transform:
                                    selected === index
                                        ? `translateX(calc(-50% - ${translateX})) translateY(-50%) translateZ(3000px) scale(0.75) rotateZ(-90deg) rotateY(86deg) rotateZ(-4deg)`
                                        : `rotateY(52deg) rotateX(-19deg) rotateZ(-22deg) translateX(calc(-50% + ${
                                              (active === index ? -120 : 0) *
                                              scaleFactor
                                          }px)) translateY(-50%) translateZ(${
                                              scaleFactor *
                                              (tapeGap * index - left)
                                          }px)`,
                                zIndex: selected === index ? 10 : 1,
                                transitionDuration:
                                    selected === index ? 2000 : 0,
                            }}
                        >
                            <div
                                className={classes.tapeFront}
                                style={{
                                    backgroundImage: `url(${window.rootUrl}/img/tapes/front/${tape.tapeNo}.webp)`,
                                }}
                            ></div>
                            <div
                                className={classes.tapeTop}
                                style={{
                                    transform: `translateY(${
                                        -80 * scaleFactor
                                    }px) rotateX(90deg)`,
                                }}
                            ></div>
                            <div
                                className={classes.tapeSpine}
                                style={{
                                    transform: `translateX(${
                                        -80 * scaleFactor
                                    }px) rotateY(-90deg)`,
                                    backgroundImage: `url(${window.rootUrl}/img/tapes/spine/${tape.tapeNo}.webp)`,
                                }}
                            ></div>
                        </div>
                    ))}
                    {selectedTapeModal && (
                        <SelectedTape
                            tape={tapes[selected]}
                            onLeave={() => onTapeLeave()}
                            onClose={() => setSelectedTapeModal(false)}
                        />
                    )}
                    <div
                        className={classes.detailWrap}
                        style={{ opacity: selectedTapeModal ? 0 : 1 }}
                    >
                        {tapes.map((tape, index) => (
                            <div
                                key={index}
                                className={classNames(
                                    classes.tapeDetail,
                                    index === active
                                        ? classes.tapeDetailActive
                                        : '',
                                    index < active
                                        ? classes.tapeDetailPrev
                                        : '',
                                    index > active ? classes.tapeDetailNext : ''
                                )}
                            >
                                <div className={classes.tapeNo}>
                                    Mixtape #{tape.tapeNo}
                                </div>
                                <div
                                    className={classes.tapeTitle}
                                    dangerouslySetInnerHTML={{
                                        __html: tape.title,
                                    }}
                                ></div>
                            </div>
                        ))}
                    </div>
                    <div
                        className={classes.arrows}
                        style={{ opacity: selectedTapeModal ? 0 : 1 }}
                    >
                        <button
                            className={classes.btnLeft}
                            onClick={goPrev}
                            disabled={active === 0}
                        >
                            <ArrowLeftIcon />
                        </button>
                        <button
                            className={classes.btnRight}
                            onClick={goNext}
                            disabled={active === tapes.length}
                        >
                            <ArrowRightIcon />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
